import { PrimaryDetailsPanel } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanel';
import { PrimaryDetailsPanelColumn } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanelColumn';
import EditIcon from '@/images/EditIcon.svg?react';
import CheckedIcon from '@/images/CheckSmallIcon.svg?react';
import DashIcon from '@/images/DashIcon.svg?react';
import { PrimaryDetailsPanelHeader } from '@/bundle/shared/components/PrimaryDetailsPanel/styles';
import { IconButton } from '@/components/IconButton/IconButton';
import capitalize from 'lodash/capitalize';
import { OrganizationStatus } from './OrganizationStatus';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { OpcoOrganizationDetailsType } from '@/api/v1/opco/organizations/getOrganizationApi';
import { SUBSCRIPTION_PLAN_TYPE } from '@/const/shared';
import { SubscriptionStatus } from '@/bundle/shared/components/SubscriptionStatus';
import { SubscriptionPendingCancellationDate } from '@/bundle/shared/components/SubscriptionPendingCancellationDateStatus';
import { Box } from '@/components/Box/Box';
import {
  getUsersLimit,
  isCancelledSubscriptionStatus,
  isPendingCancellationSubscriptionStatus,
} from '@/helpers/billingHelpers';

type OrganizationDetailsPanelType = {
  organization: OpcoOrganizationDetailsType;
  onEdit: () => void;
};

export const OrganizationDetailsPanel = ({ organization, onEdit }: OrganizationDetailsPanelType) => {
  if (!organization) return;

  const { name, status, date_created, subscription_plan_type, is_kyc_allowed, billing_profile } = organization;
  const subscription = billing_profile?.subscription;
  const cancellationDate = subscription?.current_period_end;
  const currentPlan = billing_profile?.current_plan;

  const isStandardPlan = subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.STANDARD;
  const planName = isStandardPlan && currentPlan?.name;
  const subscriptionStatus = subscription?.status;

  const panelWidth = isStandardPlan ? '100%' : '';
  const usersLimit = getUsersLimit(organization, currentPlan?.max_users);
  const isPendingCancellationSubscription = isPendingCancellationSubscriptionStatus(subscription);
  const isCancelledSubscription = isCancelledSubscriptionStatus(subscriptionStatus);
  const showCancellationStatus = isPendingCancellationSubscription && !isCancelledSubscription;

  return (
    <PrimaryDetailsPanel
      headerSlot={
        <>
          <PrimaryDetailsPanelHeader>{name}</PrimaryDetailsPanelHeader>
          <IconButton onClick={onEdit}>
            <EditIcon />
          </IconButton>
        </>
      }
      width={panelWidth}
    >
      <PrimaryDetailsPanelColumn name='Status'>
        <OrganizationStatus status={status} />
      </PrimaryDetailsPanelColumn>
      <PrimaryDetailsPanelColumn name='Creation Date'>{formatDate(date_created)}</PrimaryDetailsPanelColumn>
      <PrimaryDetailsPanelColumn name='User Limit'>{usersLimit}</PrimaryDetailsPanelColumn>
      <PrimaryDetailsPanelColumn name='Plan Type'>{capitalize(subscription_plan_type)}</PrimaryDetailsPanelColumn>
      {isStandardPlan && <PrimaryDetailsPanelColumn name='Plan'>{planName || '-'}</PrimaryDetailsPanelColumn>}
      {isStandardPlan && (
        <PrimaryDetailsPanelColumn name='Subscription Status'>
          {subscriptionStatus ? (
            <Box display='flex' alignItems='center' columnGap='6px'>
              <SubscriptionStatus status={subscriptionStatus} />{' '}
              {showCancellationStatus && <SubscriptionPendingCancellationDate date={cancellationDate} />}
            </Box>
          ) : (
            '-'
          )}
        </PrimaryDetailsPanelColumn>
      )}
      <PrimaryDetailsPanelColumn name='ID Verification'>
        {is_kyc_allowed ? <CheckedIcon /> : <DashIcon />}
      </PrimaryDetailsPanelColumn>
    </PrimaryDetailsPanel>
  );
};
